import { useMemo, useRef } from 'react';
import { Flex, GoodsCard, useScreenSize } from '@banhala/ui-kit';
import {
  VideoGoodsCard,
  FullVideoGoodsCard,
} from '../TwoColCardList/VideoGoodsCard';
import { useAtomValue } from 'jotai';
import type { RowItem } from '../../types';
import { feedbackCacheAtom } from '../../state/feedbackCache.atom';
import { GoodsCardFeedbackOverlay } from '../../../GoodsCardFeedback';
import { isAndroid, isNotNil, isWeb } from '../../../../utils';
import { useInstanceId } from '../../../../hooks';

const COLUMN_GAP = 2;
const COLUMN_COUNT = 3;

/**
 * 3개 상품을 한 행으로 렌더링 (List 중첩 없이 단일 FlatList에서 가상화)
 *
 * 기존 ThreeColGoodsList는 내부에 `<List numColumns={3}>`을 사용하여
 * 수직×수직 List 중첩이 발생 → 가상화 무력화 → 빠른 스크롤 시 흰 화면.
 * 이 컴포넌트는 3개 상품을 Flex.Row + gap으로 렌더링하여 중첩을 제거한다.
 */
export const ThreeColGoodsRow = ({
  items,
  onPressItem,
  onPressLike,
  likeMap,
  onPressMore,
  onLongPress,
  onCancelFeedback,
}: {
  items: RowItem[];
  onPressItem: (item: RowItem) => void;
  onPressLike: (item: RowItem) => Promise<void>;
  likeMap: Record<string, boolean>;
  onPressMore?: (item: RowItem) => void;
  onLongPress?: (item: RowItem) => void;
  onCancelFeedback?: (goodsSno: number) => void;
}) => {
  const { width: screenWidth } = useScreenSize();
  const feedbackCache = useAtomValue(feedbackCacheAtom);
  const instanceId = useInstanceId();
  // Android: longPress 후 onTouchEnd→onPress 연쇄 발동 방지용 플래그
  const longPressedRef = useRef(false);
  const itemWidth = useMemo(
    () => (screenWidth - COLUMN_GAP * (COLUMN_COUNT - 1)) / COLUMN_COUNT,
    [screenWidth],
  );

  return (
    <Flex.Row gap={2}>
      {items.map((item, i) => {
        if (item.type === 'placeholder') {
          return <Flex key={`ph-${i}`} flex={1} />;
        }
        if (item.type === 'primaryCard') {
          return <Flex key={item.key} flex={1} />;
        }
        if (item.type === 'videoGoods') {
          const VideoCard =
            item.cardType === 'FULL_VIDEO_GOODS_CARD'
              ? FullVideoGoodsCard
              : VideoGoodsCard;
          return (
            <VideoCard
              key={`video-${item.sno}`}
              item={item.item}
              width={itemWidth}
              isLike={likeMap[item.sno] ?? false}
              onPress={() => onPressItem(item)}
              onPressLike={async () => {
                await onPressLike(item);
              }}
            />
          );
        }
        const cached = feedbackCache.get(`${instanceId}:${item.sno}`);
        const cardData = cached
          ? {
              ...item.data,
              hasAd: isNotNil(item.ad),

              thumbnail: {
                ...item.data.thumbnail,
                blurRadius: 10,
              },
            }
          : { ...item.data, hasAd: isNotNil(item.ad) };
        return (
          <Flex key={`${item.componentIndex}-${item.entityIndex}`} flex={1}>
            <GoodsCard
              key={cached ? `${item.sno}-fb` : `${item.sno}`}
              data={cardData}
              size="medium"
              testID={`goods/${item.sno}?LIST_ID=${item.listId ?? ''}&LIST_INDEX=${item.entityIndex ?? 0}&DELIVERY_TYPE=${item.data.deliveryType}`}
              defaultWidth={itemWidth}
              onPress={() => {
                if (longPressedRef.current) {
                  longPressedRef.current = false;
                  return;
                }
                onPressItem(item);
              }}
              onPressLike={async () => {
                await onPressLike(item);
              }}
              onPressMore={
                item.hasMoreAction && item.feedbackType
                  ? () => onPressMore?.(item)
                  : undefined
              }
              onLongPress={
                !isWeb && item.hasMoreAction && item.feedbackType
                  ? () => {
                      if (isAndroid) {
                        longPressedRef.current = true;
                      }
                      onLongPress?.(item);
                    }
                  : undefined
              }
              isLike={likeMap[item.sno]}
            />
            {cached && (
              <GoodsCardFeedbackOverlay
                feedbackCache={cached}
                logging={item.logging}
                onCancelComplete={onCancelFeedback!}
              />
            )}
          </Flex>
        );
      })}
    </Flex.Row>
  );
};
